import { useEffect, useMemo, useRef, useState } from 'react'
import { LEGACY_FRAME_STRIDE, type DeepStats, type ReplayData, type ReplayRound } from '../types'
import { SectionLabel } from './RoundTimeline'
import mapTransforms from '../data/map-transforms.json'
import { fetchDrawings, replayRoundUrl, saveDrawings } from '../data/api'
import { useCanCoach } from '../data/session'

// Per-map coordinate transforms (pos_x/pos_y/scale) extracted from the CS2 overview data —
// factual values so any map's radar + positions align. Falls back to the replay's own transform.
const MAPS = mapTransforms as Record<string, { posX: number; posY: number; scale: number }>
/** Values per player per frame. Replays parsed before floors existed carry no `stride` and are 10;
 *  reading the wrong one shifts every field by a slot, so it is never assumed. */
function stride(data: ReplayData) {
  return data.stride ?? LEGACY_FRAME_STRIDE
}
/** Index of the lower-floor flag — the slot added after `defuser`. */
function loSlot(data: ReplayData) {
  return stride(data) - 1
}

function tf(data: ReplayData) {
  const m = MAPS[data.map]
  return {
    size: data.transform.size,
    posX: m?.posX ?? data.transform.posX,
    posY: m?.posY ?? data.transform.posY,
    scale: m?.scale ?? data.transform.scale,
  }
}

const CANVAS = 900 // backing resolution; the radar PNGs are 1024 so this stops throwing detail away
// Marks were sized in raw pixels against a 600px canvas — bumping CANVAS made every dot and label
// a third smaller relative to the map. Size them off this instead.
const K = CANVAS / 600
// The map is square, so its width is bounded by viewport height — leaving room for the header,
// control bar and moments row. Panels sit flush against it either side.
const MAP_W = 'min(1000px, calc(100vh - 300px))'
const SPEEDS = [1, 2, 4]
const SMOKE_LEN = 72 // ~18s @4fps
// Same palette as the scouting utility map — a smoke should look like a smoke on every screen.
const THROW_COLOR: Record<string, string> = {
  smokes: '#c2c9d4',
  molly: '#f0862f',
  he: '#e23b3b',
  flashes: '#f2df8f',
}
const FIRE_LEN = 28 // ~7s


// Fixed order so the panel doesn't reshuffle between demos; the two a coach reaches for first
// (what went wrong, what went right) lead.
const MOMENT_GROUPS = [
  'Our opening deaths',
  'Our opening kills',
  'Rounds after a timeout',
  'Our eco / force rounds',
  'Multi-kills (3+)',
  'Bomb plants',
]

export interface Stroke {
  color: string
  pts: [number, number][]
  /** Who drew it, stamped by the API on save. Absent on strokes drawn before attribution existed,
   *  and on the live stroke under the pen — it hasn't been saved yet. */
  by?: string
  byName?: string
}

export interface Moment {
  group: string
  roundNo: number
  ri: number // index into data.rounds
  frame: number
  note?: string
}

/** Everything a coach wants to jump to is already in the artifacts — it just wasn't addressable.
 *  Openings, plants and multi-kills come from the replay's own kill list; timeout and eco/force
 *  rounds come from the stats artifact. Nothing new is computed server-side. */
function computeMoments(data: ReplayData | null, deep: DeepStats | null): Moment[] {
  if (!data) return []
  const us = data.ourTeam
  const teamOf = new Map(data.players.map((p) => [p.sid, p.team]))
  const out: Moment[] = []

  data.rounds.forEach((r, ri) => {
    const kills = r.kills.filter((k) => k.weapon !== 'world').sort((a, b) => a.tf - b.tf)
    const first = kills[0]
    if (first) {
      const victimUs = teamOf.get(first.victim) === us
      out.push({
        group: victimUs ? 'Our opening deaths' : 'Our opening kills',
        roundNo: r.round,
        ri,
        frame: first.tf,
        note: first.weapon.replace(/_/g, ' '),
      })
    }
    if (r.bomb) out.push({ group: 'Bomb plants', roundNo: r.round, ri, frame: r.bomb.tf, note: r.bomb.site ?? undefined })

    // 3+ kills by one player in a round — seek to the one that made it a multi.
    const byAttacker = new Map<string, number[]>()
    for (const k of kills) {
      if (!byAttacker.has(k.attacker)) byAttacker.set(k.attacker, [])
      byAttacker.get(k.attacker)!.push(k.tf)
    }
    for (const [sid, tfs] of byAttacker) {
      if (tfs.length < 3) continue
      const who = data.players.find((p) => p.sid === sid)
      out.push({
        group: 'Multi-kills (3+)',
        roundNo: r.round,
        ri,
        frame: tfs[2],
        note: `${who?.name ?? '?'} ${tfs.length}k`,
      })
    }
  })

  const idxOf = (roundNo: number) => data.rounds.findIndex((r) => r.round === roundNo)

  for (const ev of deep?.timeouts?.events ?? []) {
    const ri = idxOf(ev.beforeRound)
    if (ri >= 0) {
      out.push({
        group: 'Rounds after a timeout',
        roundNo: ev.beforeRound,
        ri,
        frame: 0,
        note: ev.calledBy ? `${ev.calledBy} called` : undefined,
      })
    }
  }

  for (const er of deep?.economy?.rounds ?? []) {
    // The engine emits these uppercase (ECO/FORCE/FULL/PISTOL) — don't assume the casing.
    const t = (er.types?.[us] ?? '').toUpperCase()
    if (t !== 'ECO' && t !== 'FORCE') continue
    const ri = idxOf(er.round)
    if (ri >= 0) {
      out.push({ group: 'Our eco / force rounds', roundNo: er.round, ri, frame: 0, note: t.toLowerCase() })
    }
  }

  return out
}

const PEN_COLORS = ['#ffcc4d', '#4dd2ff', '#ff5d5d', '#7dff8a', '#ffffff']

/** Pointer → canvas coordinates. The canvas is displayed scaled, so client pixels aren't canvas
 *  pixels — going through the bounding rect keeps a stroke under the cursor at any map size. */
function canvasPt(e: React.PointerEvent<HTMLCanvasElement>): [number, number] {
  const r = e.currentTarget.getBoundingClientRect()
  return [((e.clientX - r.left) / r.width) * CANVAS, ((e.clientY - r.top) / r.height) * CANVAS]
}

/** Redrawn every frame on top of the replay — the playback loop repaints the whole canvas. */
function paintStrokes(cv: HTMLCanvasElement | null, done?: Stroke[], live?: Stroke | null) {
  const c = cv?.getContext('2d')
  if (!c) return
  c.save()
  c.lineWidth = 3
  c.lineCap = 'round'
  c.lineJoin = 'round'
  for (const st of [...(done ?? []), ...(live ? [live] : [])]) {
    if (st.pts.length < 2) continue
    c.strokeStyle = st.color
    c.beginPath()
    c.moveTo(st.pts[0][0], st.pts[0][1])
    for (const [x, y] of st.pts.slice(1)) c.lineTo(x, y)
    c.stroke()
  }
  c.restore()
}

export function Replay({
  url = '/replay.json',
  deepUrl,
  initialRound,
  demoId,
}: {
  url?: string
  deepUrl?: string
  initialRound?: number
  /** Given, the telestration is loaded from and saved to the server. Without it (the sample match)
   *  the pen still works, it just doesn't outlive the tab. */
  demoId?: string
}) {
  const [data, setData] = useState<ReplayData | null>(null)
  // Frames for a split replay, fetched one round at a time and kept. replay.json used to be ~5 MB
  // because it carried every round's positions, and the replayer only ever shows one — on a phone
  // that was a wait for twenty-three rounds nobody was looking at. Demos parsed before the split
  // still carry frames inline, so this stays empty for them and the code path below is skipped.
  const [roundFrames, setRoundFrames] = useState<Record<number, { frames: number[][]; inv: number[][][] }>>({})
  const [framesLoading, setFramesLoading] = useState(false)
  const [deep, setDeep] = useState<DeepStats | null>(null)
  const [group, setGroup] = useState<string | null>(null)
  const [roundIdx, setRoundIdx] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [speed, setSpeed] = useState(2)
  const [frame, setFrame] = useState(0)
  // Which floor of a two-storey map to draw. Manual, not automatic: the floor flipping under you
  // mid-round because four of five players walked down ramp is worse than choosing it yourself.
  const [layer, setLayer] = useState<'upper' | 'lower'>('upper')
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Telestration. Strokes live in canvas space (0..CANVAS), not screen pixels, so they stay put
  // when the map is resized. Per round — a drawing about A-site execute means nothing on round 12.
  const [pen, setPen] = useState(false)
  const [penColor, setPenColor] = useState('#ffcc4d')
  const [strokes, setStrokes] = useState<Record<number, Stroke[]>>({})
  // Saved drawings: loaded once per match, then written back a beat after the pen stops. A blob
  // write per stroke would be one request per flick of the wrist.
  const canCoach = useCanCoach()
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [drawingsStale, setDrawingsStale] = useState(false)
  const loadedDrawings = useRef(false)
  const strokeRef = useRef<Stroke | null>(null)
  useEffect(() => {
    loadedDrawings.current = false
    if (!demoId) return
    fetchDrawings(demoId)
      .then((d) => {
        if (Object.keys(d.byRound).length) setStrokes(d.byRound)
        setDrawingsStale(!!d.stale)
      })
      .finally(() => {
        loadedDrawings.current = true
      })
  }, [demoId])

  useEffect(() => {
    // Don't write back the empty state we started with, or a load would wipe the blob.
    if (!demoId || !canCoach || !loadedDrawings.current) return
    setSaveState('saving')
    const t = setTimeout(() => {
      saveDrawings(demoId, strokes)
        .then((stored) => {
          setSaveState('saved')
          // What came back is what exists. If the server trimmed it, showing the untrimmed version
          // until reload would be a lie about what the team will see.
          if (JSON.stringify(stored) !== JSON.stringify(strokes)) setStrokes(stored)
        })
        .catch(() => setSaveState('error'))
    }, 1200)
    return () => clearTimeout(t)
  }, [strokes, demoId, canCoach])
  const [recording, setRecording] = useState(false)
  const recRef = useRef<MediaRecorder | null>(null)
  const timeRef = useRef(0)
  const rafRef = useRef(0)
  const radarRef = useRef<HTMLImageElement | null>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)

  useEffect(() => {
    setData(null)
    setRoundFrames({})
    fetch(url)
      .then((r) => r.json())
      .then((d) => {
        setData(d)
        // Deep-linked from the round finder: open at that round rather than round 1.
        if (initialRound) {
          const i = (d.rounds ?? []).findIndex((r: ReplayRound) => r.round === initialRound)
          if (i >= 0) {
            setRoundIdx(i)
            timeRef.current = 0
            setFrame(0)
          }
        }
      })
      .catch(() => setData(null))
  }, [url, initialRound])

  // Timeout and buy-type moments come from the stats artifact; the rest are in the replay itself.
  useEffect(() => {
    setDeep(null)
    if (!deepUrl) return
    fetch(deepUrl)
      .then((r) => r.json())
      .then(setDeep)
      .catch(() => setDeep(null))
  }, [deepUrl])

  // Map radar overlay, served by the API from blob storage (/api/radar/<map>). Falls back to the
  // movement-derived backdrop if the map has no radar uploaded.
  useEffect(() => {
    radarRef.current = null
    if (!data) return
    // Guard the async load: switch matches or flip the floor fast and a late onload would install
    // the previous map's (or floor's) radar under the current frame.
    let dead = false
    const img = new Image()
    img.onload = () => {
      if (!dead) radarRef.current = img
    }
    // Two-storey maps ship a second radar for the bottom floor; one image can't show both.
    img.src = `/api/radar/${data.layered && layer === 'lower' ? `${data.map}_lower` : data.map}?v=cs2`
    return () => {
      dead = true
    }
  }, [data, layer])

  const indexRound: ReplayRound | undefined = data?.rounds[roundIdx]
  /** The round the replayer actually draws. On a split replay the index entry has empty frames
   *  until its blob arrives, so this merges the two — everything else (kills, utility, throws)
   *  comes from the index, which is why the split left those alone. */
  const round: ReplayRound | undefined = useMemo(() => {
    if (!indexRound) return undefined
    if (!data?.split) return indexRound
    const heavy = roundFrames[indexRound.round]
    return heavy ? { ...indexRound, frames: heavy.frames, inv: heavy.inv } : { ...indexRound, frames: [], inv: [] }
  }, [indexRound, data?.split, roundFrames])
  // Drawings are keyed by the round NUMBER, not its index in the array. A reparse can change how
  // many rounds there are (the parser has had round-count fixes), and an index-keyed drawing would
  // silently move to a different round — nothing about a stroke looks wrong afterwards. Notes
  // already key by number, so this is also the two agreeing.
  const roundNo = round?.round ?? roundIdx + 1
  const nFrames = round?.frames.length ?? 0

  // Fetch this round's frames, and quietly warm the next one. Without the prefetch, stepping
  // through a match with ↑/↓ stalls at every boundary — which is precisely how a coach watches, so
  // the split would have traded one long wait for twenty-four short ones.
  useEffect(() => {
    if (!data?.split || !demoId || !indexRound) return
    const wanted = [indexRound.round, data.rounds[roundIdx + 1]?.round].filter(
      (n): n is number => typeof n === 'number',
    )
    let cancelled = false
    const missing = wanted.filter((n) => !roundFrames[n])
    if (!missing.length) return
    // Only the round being watched blocks the UI; the prefetch must never show a spinner.
    if (missing.includes(indexRound.round)) setFramesLoading(true)
    Promise.all(
      missing.map(async (n) => {
        try {
          const r = await fetch(replayRoundUrl(demoId, n))
          if (!r.ok) return [n, null] as const
          return [n, (await r.json()) as { frames: number[][]; inv: number[][][] }] as const
        } catch {
          return [n, null] as const
        }
      }),
    ).then((got) => {
      if (cancelled) return
      setRoundFrames((cur) => {
        const next = { ...cur }
        for (const [n, payload] of got) if (payload) next[n] = payload
        return next
      })
      setFramesLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [data, demoId, indexRound, roundIdx, roundFrames])

  const backdrop = useMemo(() => {
    if (!data) return null
    const off = document.createElement('canvas')
    off.width = off.height = CANVAS
    const c = off.getContext('2d')!
    const p = project(data)
    c.fillStyle = 'rgba(143,188,232,0.09)'
    for (const [x, y] of data.walkspace) {
      const [cx, cy] = p(x, y)
      c.fillRect(cx, cy, 2, 2)
    }
    return off
  }, [data])

  // Distinct names on this round's saved strokes, in the order they first appear. Derived rather
  // than stored: the strokes are the truth, and a separate list would drift the first time one was
  // undone.
  const authorsHere = useMemo(
    () => [...new Set((strokes[roundNo] ?? []).map((st) => st.byName).filter((n): n is string => !!n))],
    [strokes, roundNo],
  )

  useEffect(() => {
    if (!data || !round) return
    let last = 0
    const tick = (ts: number) => {
      if (last && playing) {
        timeRef.current += speed * data.fps * ((ts - last) / 1000)
        // `nFrames - 1` is -1 on a round whose frames have not arrived, and clamping the playhead
        // to that sent a NEGATIVE frame index to everything downstream. Never below zero.
        const end = Math.max(0, nFrames - 1)
        if (timeRef.current >= end) timeRef.current = end
      }
      last = ts
      draw(canvasRef.current, data, round, backdrop, radarRef.current, timeRef.current, layer)
      paintStrokes(canvasRef.current, strokes[roundNo], strokeRef.current)
      const nf = Math.floor(timeRef.current)
      setFrame((prev) => (prev === nf ? prev : nf))
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
    // roundNo as well as roundIdx: the strokes for this round are looked up by NUMBER, and a
    // dependency list that omits it is the same bug the heatmap had — a filter that changes the
    // caption and not the canvas.
  }, [data, round, backdrop, playing, speed, nFrames, strokes, roundIdx, roundNo, layer])

  const selectRound = (i: number) => {
    setRoundIdx(i)
    timeRef.current = 0
    setPlaying(true)
  }
  /** Jump to a moment, but start a few seconds early — a coach wants the approach, not the
   *  freeze-frame of the death itself. */
  const seekTo = (ri: number, f: number) => {
    setRoundIdx(ri)
    const lead = Math.max(0, f - Math.round(3 * (data?.fps ?? 4)))
    timeRef.current = lead
    setFrame(lead)
    setPlaying(true)
  }

  const moments = useMemo(() => computeMoments(data, deep), [data, deep])

  /** Record the round to a video file. GIF would need an encoder dependency; MediaRecorder over
   *  canvas.captureStream is built into the browser, and webm/mp4 drops into Discord the same way
   *  a GIF does — better quality, smaller file. Captures whatever is on the canvas, so telestration
   *  is included. */
  const record = () => {
    const cv = canvasRef.current
    if (!cv || recording) return
    const mime = ['video/mp4', 'video/webm;codecs=vp9', 'video/webm'].find(
      (m) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m),
    )
    if (!mime) return
    const chunks: BlobPart[] = []
    const rec = new MediaRecorder(cv.captureStream(30), { mimeType: mime, videoBitsPerSecond: 4_000_000 })
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data)
    rec.onstop = () => {
      const blob = new Blob(chunks, { type: mime })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${data?.map ?? 'round'}-r${round?.round ?? 0}.${mime.startsWith('video/mp4') ? 'mp4' : 'webm'}`
      a.click()
      URL.revokeObjectURL(a.href)
      setRecording(false)
    }
    recRef.current = rec
    setRecording(true)
    // Always start the clip at the top of the round — a clip that begins mid-scrub is useless.
    scrub(0)
    setPlaying(true)
    rec.start()
  }
  const stopRecord = () => {
    recRef.current?.stop()
    recRef.current = null
  }
  // Switching tabs unmounts this component. Without a teardown the recorder kept running with no
  // owner, buffering at 4 Mbit/s, and onstop never fired — so the clip was silently lost.
  useEffect(() => () => {
    recRef.current?.stop()
    recRef.current = null
  }, [])
  // Reaching the end of the round ends the clip on its own, so a coach never has to time it.
  useEffect(() => {
    if (recording && nFrames && frame >= nFrames - 1) stopRecord()
  }, [recording, frame, nFrames])
  const scrub = (v: number) => {
    timeRef.current = v
    setFrame(Math.floor(v))
    draw(canvasRef.current, data, round, backdrop, radarRef.current, v, layer)
  }
  // Seek from a pointer x over the timeline track — same 0..1 mapping the ticks/playhead use,
  // so click position, playhead and event marks always line up.
  const seekAt = (clientX: number) => {
    const el = trackRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    setPlaying(false)
    scrub(Math.round(pct * Math.max(nFrames - 1, 0)))
  }
  const atEnd = frame >= nFrames - 1

  // Keyboard control. The replayer's whole job is scrubbing, and there was no way to step a single
  // frame — the finest the pointer track allows is a few frames per pixel — which is the most common
  // review action. Space play/pause, ←/→ step a frame, ↑/↓ (and [ ]) change round.
  useEffect(() => {
    if (!data) return
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack typing in the note field or the pen colour picker.
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
      const nf = data.rounds[roundIdx]?.frames.length ?? 0
      const step = (d: number) => {
        setPlaying(false)
        scrub(Math.max(0, Math.min(nf - 1, Math.round(timeRef.current) + d)))
      }
      const round = (d: number) => selectRound(Math.max(0, Math.min(data.rounds.length - 1, roundIdx + d)))
      switch (e.key) {
        case ' ':
          e.preventDefault()
          if (frame >= nf - 1) {
            scrub(0)
            setPlaying(true)
          } else {
            setPlaying((p) => !p)
          }
          break
        case 'ArrowLeft':
          e.preventDefault()
          step(e.shiftKey ? -data.fps : -1) // Shift = one second
          break
        case 'ArrowRight':
          e.preventDefault()
          step(e.shiftKey ? data.fps : 1)
          break
        case 'ArrowUp':
        case '[':
          e.preventDefault()
          round(-1)
          break
        case 'ArrowDown':
        case ']':
          e.preventDefault()
          round(1)
          break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, roundIdx, frame])

  const roundKills = round ? round.kills.filter((k) => k.tf <= frame && k.weapon !== 'world') : []
  const strip: { tf: number; c: string; big: boolean }[] = round
    ? [
        ...round.kills.filter((k) => k.weapon !== 'world').map((k) => ({ tf: k.tf, c: UTIL.kill, big: true })),
        ...round.smokes.map((s) => ({ tf: s.s, c: UTIL.smoke, big: false })),
        ...round.fires.map((f) => ({ tf: f.s, c: UTIL.molotov, big: false })),
        ...round.flashes.map((f) => ({ tf: f.tf, c: UTIL.flash, big: false })),
        ...round.hes.map((h) => ({ tf: h.tf, c: UTIL.he, big: false })),
      ]
    : []

  return (
    <section className="border border-border bg-surface p-5" style={{ borderRadius: 'var(--radius)' }}>
      <div className="flex items-center justify-between">
        <SectionLabel n="03" label="2D replay" />
        {data && (
          <span className="text-xs text-text-faint">
            {data.map} · round {round?.round}/{data.rounds.length}
            {/* A round whose frames haven't arrived draws an empty map. Saying so beats an empty
                map that looks like a broken one — the whole-file download had the honesty of being
                slow in one obvious place, and this has to buy that back. */}
            {framesLoading && <span className="text-text-dim"> · loading round…</span>}
          </span>
        )}
      </div>

      {!data ? (
        <div className="mt-6 text-sm text-text-dim">Loading replay…</div>
      ) : (
        <>
          {/* Flex, not a 1fr grid: the middle column stretched to the page while the canvas capped
              out and centred inside it, so all the slack fell BETWEEN the map and the panels. As a
              centred group the map and panels stay shoulder to shoulder and the slack goes outside. */}
          <div className="mt-4 flex flex-col items-center gap-3 lg:flex-row lg:items-start lg:justify-center">
            <TeamPanel data={data} round={round!} frame={frame} team={data.players[0].team} align="left" />
            <div className="w-full" style={{ maxWidth: MAP_W }}>
              <canvas
                ref={canvasRef}
                width={CANVAS}
                height={CANVAS}
                onPointerDown={(e) => {
                  if (!pen) return
                  e.currentTarget.setPointerCapture(e.pointerId)
                  strokeRef.current = { color: penColor, pts: [canvasPt(e)] }
                }}
                onPointerMove={(e) => {
                  if (!pen || !strokeRef.current) return
                  strokeRef.current.pts.push(canvasPt(e))
                }}
                onPointerUp={() => {
                  const st = strokeRef.current
                  strokeRef.current = null
                  if (!st || st.pts.length < 2) return
                  setStrokes((prev) => ({ ...prev, [roundNo]: [...(prev[roundNo] ?? []), st] }))
                }}
                className="block w-full border border-border"
                style={{
                  borderRadius: 'var(--radius)',
                  background: 'var(--bg-2)',
                  aspectRatio: '1',
                  cursor: pen ? 'crosshair' : 'default',
                  touchAction: pen ? 'none' : 'auto',
                }}
              />

              <div className="mt-3 flex items-center gap-2">
                {/* Fixed width: the label changes play→pause→replay, and letting it resize shifted
                    the whole track (and every event tick on it) sideways mid-playback. */}
                <button
                  onClick={() => (atEnd ? (scrub(0), setPlaying(true)) : setPlaying((p) => !p))}
                  title={atEnd ? 'Replay the round' : playing ? 'Pause' : 'Play'}
                  className="flex h-7 w-9 shrink-0 items-center justify-center border border-border-bright text-sm text-accent hover:bg-surface-2"
                  style={{ borderRadius: 'var(--radius)' }}
                >
                  {atEnd ? '↻' : playing ? '❚❚' : '►'}
                </button>
                <button
                  onClick={() => {
                    const on = !pen
                    setPen(on)
                    if (on) setPlaying(false)
                  }}
                  title="Draw on the map (pauses playback)"
                  className="flex h-7 w-9 shrink-0 items-center justify-center text-sm"
                  style={{
                    borderRadius: 'var(--radius)',
                    border: '1px solid var(--border)',
                    background: pen ? 'var(--accent)' : 'transparent',
                    color: pen ? 'var(--accent-ink)' : 'var(--text-dim)',
                  }}
                >
                  ✎
                </button>
                <button
                  onClick={() => (recording ? stopRecord() : record())}
                  title={recording ? 'Stop and save the clip' : 'Record this round to a video file'}
                  className="flex h-7 w-9 shrink-0 items-center justify-center text-sm"
                  style={{
                    borderRadius: 'var(--radius)',
                    border: '1px solid var(--border)',
                    background: recording ? 'var(--loss)' : 'transparent',
                    color: recording ? '#fff' : 'var(--text-dim)',
                  }}
                >
                  {recording ? '■' : '●'}
                </button>
                {data.layered && (
                  <button
                    onClick={() => setLayer((l) => (l === 'upper' ? 'lower' : 'upper'))}
                    title={`Showing the ${layer} floor — click for the other. Players on the far floor stay on the map, dimmed.`}
                    className="flex h-7 w-20 shrink-0 items-center justify-center text-xs text-text-dim hover:bg-surface-2"
                    style={{ borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}
                  >
                    {layer === 'upper' ? '▲ upper' : '▼ lower'}
                  </button>
                )}
                {/* unified timeline: baseline + played fill + event ticks + draggable playhead,
                    all on one track so click, playhead and marks line up exactly */}
                <div
                  ref={trackRef}
                  role="slider"
                  tabIndex={0}
                  aria-label="Timeline — arrow keys to scrub, space to play"
                  aria-valuemin={0}
                  aria-valuemax={Math.max(nFrames - 1, 0)}
                  aria-valuenow={frame}
                  className="relative h-7 flex-1 cursor-pointer touch-none select-none rounded outline-none focus-visible:ring-1 focus-visible:ring-accent"
                  onPointerDown={(e) => {
                    draggingRef.current = true
                    e.currentTarget.setPointerCapture(e.pointerId)
                    seekAt(e.clientX)
                  }}
                  onPointerMove={(e) => draggingRef.current && seekAt(e.clientX)}
                  onPointerUp={(e) => {
                    draggingRef.current = false
                    e.currentTarget.releasePointerCapture(e.pointerId)
                  }}
                >
                  <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-border" />
                  <div
                    className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full"
                    style={{ width: `${(frame / Math.max(nFrames - 1, 1)) * 100}%`, background: 'var(--accent)' }}
                  />
                  {strip.map((e, idx) => (
                    <span
                      key={idx}
                      className={`absolute top-1/2 w-0.5 -translate-x-1/2 -translate-y-1/2 ${e.big ? 'h-4' : 'h-2.5'}`}
                      style={{ left: `${(e.tf / Math.max(nFrames - 1, 1)) * 100}%`, background: e.c }}
                    />
                  ))}
                  <div
                    className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2"
                    style={{ left: `${(frame / Math.max(nFrames - 1, 1)) * 100}%`, background: 'var(--accent-bright)', borderColor: 'var(--surface)' }}
                  />
                </div>
                <span className="w-10 text-right text-xs tabular-nums text-text-dim">
                  {(frame / data.fps).toFixed(0)}s
                </span>
                <div className="flex gap-1">
                  {SPEEDS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSpeed(s)}
                      className="px-2 py-1 text-xs"
                      style={{
                        borderRadius: 'var(--radius)',
                        color: speed === s ? 'var(--accent-ink)' : 'var(--text-dim)',
                        background: speed === s ? 'var(--accent)' : 'transparent',
                        border: '1px solid var(--border)',
                      }}
                    >
                      {s}×
                    </button>
                  ))}
                </div>
              </div>

              {/* Only while drawing — a colour row that's always there is clutter, and one that
                  appears inside the control bar would shift the track again. */}
              {pen && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {PEN_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setPenColor(c)}
                      title={c}
                      className="h-5 w-5"
                      style={{
                        borderRadius: '50%',
                        background: c,
                        outline: penColor === c ? '2px solid var(--text)' : '1px solid var(--border)',
                        outlineOffset: '1px',
                      }}
                    />
                  ))}
                  <button
                    onClick={() =>
                      setStrokes((prev) => ({ ...prev, [roundNo]: (prev[roundNo] ?? []).slice(0, -1) }))
                    }
                    disabled={!strokes[roundNo]?.length}
                    className="px-2 py-1 text-xs text-text-dim hover:text-accent disabled:opacity-40"
                  >
                    undo
                  </button>
                  <button
                    onClick={() => setStrokes((prev) => ({ ...prev, [roundNo]: [] }))}
                    disabled={!strokes[roundNo]?.length}
                    className="px-2 py-1 text-xs text-text-faint hover:text-loss disabled:opacity-40"
                  >
                    clear
                  </button>
                  {authorsHere.length > 0 && (
                    /* One shared canvas, last save wins — so "whose line is this" was unanswerable
                       and two coaches could quietly overwrite each other. Naming them doesn't stop
                       that, but it turns a silent conflict into a visible one. */
                    <span className="text-[11px] text-text-faint" title="Who has drawn on this round">
                      drawn by {authorsHere.join(', ')}
                    </span>
                  )}
                  <span className="text-[11px] text-text-faint">
                    round {round?.round}
                    {drawingsStale
                      ? ' · drawn before this demo was reparsed'
                      : !demoId
                      ? ' · not saved (sample match)'
                      : !canCoach
                        ? ' · yours to draw, coaches save'
                        : saveState === 'saving'
                          ? ' · saving…'
                          : saveState === 'error'
                            ? ' · save failed'
                            : saveState === 'saved'
                              ? ' · saved for the team'
                              : ' · saves automatically'}
                  </span>
                </div>
              )}
            </div>
            <TeamPanel data={data} round={round!} frame={frame} team={data.players[9].team} align="right" />
          </div>

          {/* Moments — the point is to skip the 40 minutes and land on what matters. */}
          {moments.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-[11px] uppercase tracking-wider text-text-faint">Moments</div>
              <div className="flex flex-wrap gap-1.5">
                {MOMENT_GROUPS.filter((g) => moments.some((m) => m.group === g)).map((g) => {
                  const n = moments.filter((m) => m.group === g).length
                  const on = group === g
                  return (
                    <button
                      key={g}
                      onClick={() => setGroup(on ? null : g)}
                      className="px-2.5 py-1 text-xs"
                      style={{
                        borderRadius: 'var(--radius)',
                        border: '1px solid var(--border)',
                        background: on ? 'var(--accent)' : 'transparent',
                        color: on ? 'var(--accent-ink)' : 'var(--text-dim)',
                      }}
                    >
                      {g} <span className="opacity-70">({n})</span>
                    </button>
                  )
                })}
              </div>
              {group && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {moments
                    .filter((m) => m.group === group)
                    .sort((a, b) => a.roundNo - b.roundNo)
                    .map((m, i) => (
                      <button
                        key={`${m.roundNo}-${i}`}
                        onClick={() => seekTo(m.ri, m.frame)}
                        title={m.note ? `Round ${m.roundNo} — ${m.note}` : `Round ${m.roundNo}`}
                        className="border border-border px-2 py-1 text-[11px] text-text-dim hover:border-accent hover:text-accent"
                        style={{ borderRadius: 'var(--radius)' }}
                      >
                        R{m.roundNo}
                        {m.note ? <span className="ml-1 opacity-60">{m.note}</span> : null}
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* round selector + kill feed */}
          <div className="mt-4 grid gap-4 md:grid-cols-[1fr_240px]">
            <div>
              <div className="mb-2 text-[11px] uppercase tracking-wider text-text-faint">Rounds</div>
              <div className="flex flex-wrap gap-1">
                {data.rounds.map((r, i) => {
                  const won = r.winnerTeam === data.ourTeam
                  const active = i === roundIdx
                  return (
                    <button
                      key={r.round}
                      onClick={() => selectRound(i)}
                      title={`Round ${r.round} · ${r.winnerTeam}`}
                      className="font-display h-7 w-7 text-xs"
                      style={{
                        borderRadius: 'var(--radius)',
                        color: active ? 'var(--accent-ink)' : '#fff',
                        background: active ? 'var(--accent-bright)' : won ? 'var(--accent)' : 'var(--loss)',
                        outline: active ? '2px solid var(--text)' : 'none',
                      }}
                    >
                      {r.round}
                    </button>
                  )
                })}
              </div>
            </div>
            <div>
              <div className="mb-2 text-[11px] uppercase tracking-wider text-text-faint">Kill feed</div>
              <div className="space-y-1">
                {roundKills.slice(-7).reverse().map((k, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setPlaying(false)
                      scrub(k.tf)
                    }}
                    title="Jump to this kill"
                    className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-xs hover:bg-surface-2"
                  >
                    <span style={{ color: teamColor(data, k.attacker) }}>{name(data, k.attacker)}</span>
                    <span className="text-text-faint">{shortWeapon(k.weapon)}</span>
                    {k.hs && <span className="text-loss" title="headshot">◉</span>}
                    <span style={{ color: teamColor(data, k.victim) }}>{name(data, k.victim)}</span>
                  </button>
                ))}
                {!roundKills.length && <div className="text-xs text-text-faint">—</div>}
              </div>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-text-faint">
            <b>Keys:</b> space play/pause · ←/→ step a frame (Shift = 1s) · ↑/↓ or [ ] change round.
          </p>
          <p className="mt-1 text-[11px] text-text-faint">
            Positions, HP, weapons, money &amp; utility parsed from the demo. Map radar overlay loaded
            from storage; falls back to a movement-derived backdrop when a radar isn&apos;t available.
          </p>
        </>
      )}
    </section>
  )
}

// Armor state: none (·) / vest / vest+helmet — a filled vest, with a helmet arc when helmeted.
function ArmorIcon({ armor, helmet }: { armor: number; helmet: number }) {
  if (!armor && !helmet)
    return <span className="w-3 shrink-0 text-center text-[8px] leading-none text-text-faint">·</span>
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden className="shrink-0">
      {helmet ? <path d="M2.6 5 a3.4 3.4 0 0 1 6.8 0" fill="none" stroke="var(--accent)" strokeWidth="1.3" /> : null}
      <path
        d="M3 5 h6 v3 l-3 2.4 l-3 -2.4 z"
        fill={armor ? 'var(--accent-strong)' : 'none'}
        stroke="var(--accent-strong)"
        strokeWidth="0.9"
      />
    </svg>
  )
}

function TeamPanel({
  data,
  round,
  frame,
  team,
  align,
}: {
  data: ReplayData
  round: ReplayRound
  frame: number
  team: string
  align: 'left' | 'right'
}) {
  const S = stride(data)
  // Clamped at BOTH ends. `Math.min(frame, length - 1)` alone goes negative the moment the round
  // has no frames yet — a split replay's index entry carries `frames: []` until its blob lands,
  // which makes `length - 1` = -1 — and `frames[-1]` is undefined, so the first `f[…]` below threw
  // and took the whole page down with it. Every demo parsed since the split shipped (5 Aug) opens
  // through that window, and `frame` itself goes to -1 too: the playback loop clamps the playhead
  // to `nFrames - 1`, which is -1 on an empty round.
  const fi = Math.max(0, Math.min(frame, round.frames.length - 1))
  const f: number[] | undefined = round.frames[fi]
  const invFrame = round.inv?.[Math.min(fi, (round.inv?.length ?? 1) - 1)] ?? []
  // round K/D up to the current frame
  const kd: Record<string, { k: number; d: number }> = {}
  for (const k of round.kills) {
    if (k.tf > frame) continue
    if (k.weapon !== 'world') (kd[k.attacker] ??= { k: 0, d: 0 }).k++
    ;(kd[k.victim] ??= { k: 0, d: 0 }).d++
  }
  const heading = (
    <div className={`font-display text-sm font-semibold ${align === 'right' ? 'text-right' : ''}`}
      style={{ color: data.players.find((p) => p.team === team)?.color }}>
      {team}
    </div>
  )
  // No frame to read: this round's blob is still in flight. The side keeps its name and says so,
  // rather than inventing an HP bar or — as it did — throwing on the first player it reached.
  if (!f) {
    return (
      <div className="w-full shrink-0 space-y-2 lg:w-60">
        {heading}
        <div className={`text-xs text-text-faint ${align === 'right' ? 'text-right' : ''}`}>loading round…</div>
      </div>
    )
  }

  return (
    <div className="w-full shrink-0 space-y-2 lg:w-60">
      {heading}
      {data.players
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => p.team === team)
        .map(({ p, i }) => {
          const o = i * S
          const alive = f[o + 3]
          const hp = f[o + 4]
          const money = f[o + 5]
          const activeName = data.weapons[f[o + 6]] ?? ''
          const armor = f[o + 7] ?? 0
          const helm = f[o + 8] ?? 0
          const kit = f[o + 9] ?? 0
          const kdp = kd[p.sid] ?? { k: 0, d: 0 }
          const items = (invFrame[i] ?? []).map((wi) => data.weapons[wi]).filter(Boolean)
          const guns = items.filter((w) => weaponCat(w) === 'primary' || weaponCat(w) === 'pistol')
          const nades = items.filter((w) => weaponCat(w) === 'nade')
          const hasBomb = items.some((w) => weaponCat(w) === 'bomb')
          return (
            <div
              key={p.sid}
              className="border border-border bg-bg-2 px-2.5 py-1.5"
              style={{ borderRadius: 'var(--radius)', opacity: alive ? 1 : 0.4 }}
            >
              {/* name · K/D */}
              <div className="flex items-center justify-between gap-1 text-xs">
                <span className="truncate text-text" style={{ color: p.color }}>
                  {p.name}
                </span>
                <span className="shrink-0 tabular-nums text-text-faint">{kdp.k}-{kdp.d}</span>
              </div>
              {/* HP bar + value */}
              <div className="mt-0.5 flex items-center gap-1">
                <div className="h-1.5 flex-1 overflow-hidden bg-border" style={{ borderRadius: '2px' }}>
                  <div className="h-full" style={{ width: `${alive ? hp : 0}%`, background: p.color }} />
                </div>
                <span className="w-5 shrink-0 text-right text-[9px] tabular-nums text-text-dim">{alive ? hp : '☠'}</span>
              </div>
              {/* armor amount + state (none / vest / vest+helmet) + kit */}
              <div className="mt-0.5 flex items-center gap-1">
                <div className="h-1.5 flex-1 overflow-hidden bg-border" style={{ borderRadius: '2px' }}>
                  <div
                    className="h-full"
                    style={{ width: `${alive ? armor : 0}%`, background: helm ? 'var(--accent)' : 'var(--accent-strong)' }}
                  />
                </div>
                <ArmorIcon armor={alive ? armor : 0} helmet={alive ? helm : 0} />
                {kit ? <span title="Defuse kit" className="text-[9px] leading-none text-win">⛊</span> : null}
              </div>
              {/* loadout: guns (active highlighted) · nade dots · C4 — money never clips */}
              <div className="mt-1 flex items-center gap-1">
                <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                  {alive &&
                    guns.map((w, gi) => (
                      <span
                        key={gi}
                        title={w}
                        className="shrink-0 rounded px-1 py-px text-[9px] leading-tight"
                        style={
                          w === activeName
                            ? { background: 'var(--accent)', color: 'var(--accent-ink)' }
                            : { background: 'var(--border)', color: 'var(--text-dim)' }
                        }
                      >
                        {shortWeapon(w)}
                      </span>
                    ))}
                  {alive && nades.length > 0 && (
                    <span className="flex shrink-0 items-center gap-0.5">
                      {nades.map((n, ni) => (
                        <span
                          key={ni}
                          title={n}
                          className="inline-block h-1.5 w-1.5 rounded-full"
                          style={{ background: NADES[n] ?? '#888' }}
                        />
                      ))}
                    </span>
                  )}
                  {alive && hasBomb && (
                    <span className="shrink-0 rounded px-1 text-[8px] font-bold leading-tight" style={{ background: 'var(--loss)', color: '#fff' }}>
                      C4
                    </span>
                  )}
                </div>
                <span className="shrink-0 text-[10px] tabular-nums text-win">${money}</span>
              </div>
            </div>
          )
        })}
    </div>
  )
}

// ---- helpers ----
function project(data: ReplayData) {
  const { posX, posY, scale, size } = tf(data)
  return (x: number, y: number): [number, number] => [
    ((x - posX) / scale / size) * CANVAS,
    ((posY - y) / scale / size) * CANVAS,
  ]
}
const R = (u: number, d: ReplayData) => {
  const t = tf(d)
  return (u / t.scale / t.size) * CANVAS
}
function name(d: ReplayData, sid: string) {
  return d.players.find((p) => p.sid === sid)?.name ?? '?'
}
function teamColor(d: ReplayData, sid: string) {
  return d.players.find((p) => p.sid === sid)?.color ?? 'var(--text-dim)'
}
const SHORT: Record<string, string> = {
  'Desert Eagle': 'Deagle', 'Dual Berettas': 'Dualies', 'Five-SeveN': 'Five7', 'CZ75-Auto': 'CZ',
  'R8 Revolver': 'R8', 'Galil AR': 'Galil', 'SG 553': 'SG553', 'SSG 08': 'Scout', 'PP-Bizon': 'Bizon',
}
function shortWeapon(w: string) {
  if (!w || w === 'nan' || w === '-') return ''
  if (SHORT[w]) return SHORT[w]
  return w.replace('weapon_', '').replace('_', ' ')
}

// Shared utility palette — kept deliberately distinct so molotov (orange) never reads as HE (red).
const UTIL = {
  smoke: '#c2c9d4',
  molotov: '#f0862f', // orange
  he: '#e23b3b', // red
  flash: '#f2df8f', // pale yellow
  decoy: '#7d8ea0',
  kill: '#ff5a5a',
}

// Loadout categorisation for the side panels. Nade → colour dot; pistol/primary → chip; C4 → badge.
const NADES: Record<string, string> = {
  Flashbang: UTIL.flash, Smoke: UTIL.smoke, HE: UTIL.he, Molotov: UTIL.molotov, Incendiary: UTIL.molotov, Decoy: UTIL.decoy,
}
const PISTOLS = new Set([
  'Glock-18', 'USP-S', 'P2000', 'P250', 'Five-SeveN', 'Tec-9', 'CZ75-Auto', 'Dual Berettas',
  'Desert Eagle', 'R8 Revolver', 'Zeus x27',
])
function weaponCat(w: string): 'nade' | 'pistol' | 'bomb' | 'primary' {
  if (w in NADES) return 'nade'
  if (w === 'C4 Explosive') return 'bomb'
  if (PISTOLS.has(w)) return 'pistol'
  return 'primary'
}

function draw(
  canvas: HTMLCanvasElement | null,
  data: ReplayData | null,
  round: ReplayRound | undefined,
  backdrop: HTMLCanvasElement | null,
  radar: HTMLImageElement | null,
  t: number,
  layer: 'upper' | 'lower' = 'upper',
) {
  if (!canvas || !data || !round) return
  const ctx = canvas.getContext('2d')!
  const p = project(data)
  const S = stride(data)
  const LO = loSlot(data)
  // On a flat map everything is "on this floor"; on nuke, `lo` says which one.
  const here = (lo?: number) => !data.layered || (lo ? 'lower' : 'upper') === layer
  const i = Math.floor(t)
  const frac = t - i
  ctx.clearRect(0, 0, CANVAS, CANVAS)
  if (radar) {
    // The official radars are bright and saturated; at full strength the player dots disappear into
    // them. Dim it — the map is context, the players are what you're actually watching.
    ctx.save()
    ctx.globalAlpha = 0.5
    ctx.drawImage(radar, 0, 0, CANVAS, CANVAS)
    ctx.restore()
  }
  else if (backdrop) ctx.drawImage(backdrop, 0, 0)

  // molotov / incendiary — burning orange area with a live flicker
  for (const fr of round.fires) {
    if (here(fr.lo) && i >= fr.s && i <= Math.min(fr.e, fr.s + FIRE_LEN)) {
      const [cx, cy] = p(fr.x, fr.y)
      const r = R(140, data)
      const flick = 0.34 + 0.14 * Math.abs(Math.sin(i * 0.9))
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r)
      g.addColorStop(0, `rgba(245,150,50,${flick})`)
      g.addColorStop(0.6, 'rgba(230,110,40,0.3)')
      g.addColorStop(1, 'rgba(210,80,35,0)')
      ctx.fillStyle = g
      circle(ctx, cx, cy, r)
    }
  }
  // smoke — dense grey puff with a soft edge (reads as cover), drawn over fire
  for (const sm of round.smokes) {
    if (here(sm.lo) && i >= sm.s && i <= Math.min(sm.e, sm.s + SMOKE_LEN)) {
      const [cx, cy] = p(sm.x, sm.y)
      const r = R(144, data)
      const g = ctx.createRadialGradient(cx, cy, r * 0.55, cx, cy, r)
      g.addColorStop(0, 'rgba(200,206,216,0.82)')
      g.addColorStop(0.85, 'rgba(188,195,206,0.62)')
      g.addColorStop(1, 'rgba(180,188,200,0)')
      ctx.fillStyle = g
      circle(ctx, cx, cy, r)
    }
  }
  // HE — quick red pop (distinct red, not the molotov orange)
  for (const he of round.hes) {
    const dt = i - he.tf
    if (here(he.lo) && dt >= 0 && dt <= 2) {
      const [cx, cy] = p(he.x, he.y)
      ctx.fillStyle = `rgba(226,59,59,${0.55 - dt * 0.18})`
      circle(ctx, cx, cy, R(55, data) + dt * 3)
    }
  }
  // flash — expanding pale-yellow ring
  for (const fl of round.flashes) {
    const dt = i - fl.tf
    if (here(fl.lo) && dt >= 0 && dt <= 2) {
      const [cx, cy] = p(fl.x, fl.y)
      ctx.strokeStyle = `rgba(242,223,143,${0.9 - dt * 0.3})`
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(cx, cy, (4 + dt * 7) * K, 0, Math.PI * 2)
      ctx.stroke()
    }
  }

  // Grenades in the air. The landed effects above say WHERE utility ended up; this is the throw
  // itself — the arc back to the thrower is the lineup, which is the half you can copy or punish.
  for (const th of round.throws ?? []) {
    if (i < th.s || i > th.e || !th.pts.length) continue
    const k = Math.min(th.pts.length - 1, i - th.s)
    const cur = th.pts[k]
    if (!here(cur[2])) continue
    const col = THROW_COLOR[th.t] ?? '#ccc'
    // Trail covers only the distance flown so far, so it reads as motion rather than a static line.
    ctx.save()
    ctx.setLineDash([4 * K, 3 * K])
    ctx.strokeStyle = withA(col, 0.55)
    ctx.lineWidth = 1.4 * K
    ctx.beginPath()
    let pen = false
    for (let j = 0; j <= k; j++) {
      if (!here(th.pts[j][2])) { pen = false; continue }
      const [tx, ty] = p(th.pts[j][0], th.pts[j][1])
      if (pen) ctx.lineTo(tx, ty)
      else ctx.moveTo(tx, ty)
      pen = true
    }
    ctx.stroke()
    ctx.restore()
    // The grenade itself, ringed so a pale flashbang still reads over a light callout.
    const [gx, gy] = p(cur[0], cur[1])
    ctx.fillStyle = 'rgba(4,6,10,0.8)'
    circle(ctx, gx, gy, 3.6 * K)
    ctx.fillStyle = col
    circle(ctx, gx, gy, 2.4 * K)
  }

  // deaths
  for (const k of round.kills) {
    if (here(k.lo) && k.tf <= i && (k.x || k.y)) {
      const [cx, cy] = p(k.x, k.y)
      ctx.strokeStyle = 'rgba(255,90,90,0.85)'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.moveTo(cx - 3 * K, cy - 3 * K); ctx.lineTo(cx + 3 * K, cy + 3 * K)
      ctx.moveTo(cx + 3 * K, cy - 3 * K); ctx.lineTo(cx - 3 * K, cy + 3 * K)
      ctx.stroke()
    }
  }

  // bomb planted
  if (round.bomb && i >= round.bomb.tf && here(round.bomb.lo)) {
    // A literal, not var(--loss): canvas fillStyle does not resolve CSS variables, so the
    // assignment was silently ignored and whatever was set last won — usually a smoke's radial
    // gradient, which is transparent out at the corner where this label sits. "BOMB A" was
    // invisible in most rounds.
    ctx.fillStyle = '#d67676'
    ctx.font = `${Math.round(11 * K)}px Inter`
    // site is null when the map has no reference points — "BOMB" beats printing a raw entity id.
    ctx.fillText(round.bomb.site ? `BOMB ${round.bomb.site}` : 'BOMB', 8 * K, CANVAS - 8 * K)
  }

  // players
  //
  // Nothing to draw before this round's frames arrive, and nothing safe either: an empty `frames`
  // makes both lookups below undefined and every read off them throws. The map, utility and bomb
  // above are all drawn from the index entry, which is present from the start — so the radar still
  // paints and only the dots wait.
  if (!round.frames.length) return
  const fi = Math.max(0, Math.min(i, round.frames.length - 1))
  const f0 = round.frames[fi]
  const f1 = round.frames[Math.min(fi + 1, round.frames.length - 1)]
  // Off-floor players stay on screen, deliberately. Hiding them would be honest about the radar
  // but useless to watch — five men vanishing into ramp is exactly the moment you're reviewing.
  // They're drawn small, dim and coneless instead: present, clearly elsewhere, not mistakable for
  // someone standing on the floor you're looking at.
  const order = data.players.map((pl, pi) => ({ pl, pi })).sort(
    (a, b) => Number(here(f0[a.pi * S + LO])) - Number(here(f0[b.pi * S + LO])),
  )
  for (const { pl, pi } of order) {
    const o = pi * S
    if (!f0[o + 3]) continue
    const on = here(f0[o + LO])
    const x = f0[o] + (f1[o] - f0[o]) * frac
    const y = f0[o + 1] + (f1[o + 1] - f0[o + 1]) * frac
    const [cx, cy] = p(x, y)
    const rad = (-f0[o + 2] * Math.PI) / 180
    if (on) {
      // view cone in the aim direction (yaw) — meaningless through a ceiling, so on-floor only
      const coneR = R(560, data)
      const half = (52 * Math.PI) / 180 // ~104° FOV
      const cg = ctx.createRadialGradient(cx, cy, 4, cx, cy, coneR)
      cg.addColorStop(0, withA(pl.color, 0.3))
      cg.addColorStop(1, withA(pl.color, 0))
      ctx.fillStyle = cg
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.arc(cx, cy, coneR, rad - half, rad + half)
      ctx.closePath()
      ctx.fill()
    }
    ctx.save()
    if (!on) ctx.globalAlpha = 0.34
    const dot = on ? 5 : 3
    // Player dot: dark ring first so the colour reads against light callouts and dark space alike.
    ctx.fillStyle = 'rgba(4,6,10,0.75)'
    circle(ctx, cx, cy, (dot + 2) * K)
    ctx.fillStyle = pl.color
    circle(ctx, cx, cy, dot * K)
    // Names only on this floor — ten labels for five visible players is worse than none.
    if (on) {
      ctx.font = `${Math.round(10 * K)}px Inter`
      ctx.lineWidth = 3 * K
      ctx.strokeStyle = 'rgba(4,6,10,0.8)'
      ctx.lineJoin = 'round'
      ctx.strokeText(pl.name, cx + 9 * K, cy + 3.5 * K)
      ctx.fillStyle = 'rgba(245,247,250,0.95)'
      ctx.fillText(pl.name, cx + 9 * K, cy + 3.5 * K)
    }
    ctx.restore()
  }
}

// #rrggbb → rgba() with alpha, for the team-coloured view cones.
function withA(hex: string, a: number) {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fill()
}
